// Shared by the Sidekiq queue admin pages (intersection coordination queue,
// timed queues): the auto-refresh choices for their polled stats, the
// scan-depth choices sent as `sample_limit` (Sidekiq::QueueInspector.sample_limit_from),
// and the note saying what a scan covered.
import { count } from 'pages/admin/shared/formatters';
import type { ScanTotals } from './types';

export const POLLING_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '30s', value: 30_000 },
  { label: '1m', value: 60_000 },
  { label: '2m', value: 120_000 },
] as const;

export const DEFAULT_POLLING_INTERVAL = 60_000; // 1 minute

export const SCAN_DEPTH_OPTIONS = [
  { id: '5000', label: '5k' },
  { id: '10000', label: '10k' },
  { id: '50000', label: '50k' },
  { id: '100000', label: '100k' },
  { id: 'all', label: 'All' },
] as const;

export type ScanDepth = (typeof SCAN_DEPTH_OPTIONS)[number]['id'];

export const DEFAULT_SCAN_DEPTH: ScanDepth = '5000';

export interface ScanNoteOptions {
  /** What the scan reads, e.g. "jobs" or "entries". */
  noun?: string;
  /**
   * True for a queue scan, which reads the newest jobs first. There a repeat
   * means a job was pushed during the scan and shifted the list, so one new
   * job went unread. A failure-set repeat is only the set scan returning an
   * entry twice, and nothing is missed.
   */
  newestFirst?: boolean;
  /**
   * For a failure scan: entries read but left out of the table because the
   * breakdown had already reached its limit of distinct error groups.
   */
  ungrouped?: number;
}

/**
 * Says plainly whether a scan counted every job or only a sample, and why it
 * stopped. A live queue keeps moving while it is read, so a full scan can
 * count more or fewer jobs than it held when the scan started; that is called
 * out too, as are entries read twice, which are counted once. The text is the
 * body of the note; a sampled scan's note is titled "Sampled" by the caller.
 *
 * @param {ScanTotals} totals - The scan's size at start, entries read, repeats skipped and stop flags.
 * @param {ScanNoteOptions} options - The noun to use, whether the scan reads newest first, and how many entries went ungrouped.
 * @return {string} The note to show above the scan results.
 */
export const scanNote = (
  { totalSize, scanned, duplicatesSkipped, sampled, timedOut }: ScanTotals,
  { noun = 'jobs', newestFirst = false, ungrouped = 0 }: ScanNoteOptions = {}
): string => {
  let repeats = '';
  if (duplicatesSkipped > 0) {
    repeats = newestFirst
      ? ` About ${count(duplicatesSkipped)} ${noun} enqueued during the scan are not included.`
      : ` ${count(duplicatesSkipped)} ${noun} returned twice by the scan were counted once.`;
  }
  if (ungrouped > 0) {
    repeats += ` ${count(ungrouped)} ${noun} were read but are not in the table: the breakdown had reached its limit of error groups.`;
  }
  const read = `${newestFirst ? 'the newest ' : ''}${count(scanned)} of ${count(totalSize)} ${noun}`;
  const coverage = `The counts below cover those ${count(scanned)} only`;

  if (timedOut) {
    return `The scan hit its time limit after reading ${read}. ${coverage}; choose a smaller scan depth for a quicker answer.${repeats}`;
  }
  if (sampled) {
    return `Read ${read}. ${coverage}; choose "All" to count every one.${repeats}`;
  }
  // A queue scan with repeats saw jobs pushed mid-scan; only if fewer were
  // read than were there at the start did some run (pop) while it was read.
  if (duplicatesSkipped > 0 && newestFirst) {
    if (scanned >= totalSize) {
      return `Counted the ${count(scanned)} ${noun} present when the scan started.${repeats}`;
    }
    return `Counted ${count(scanned)} of the ${count(totalSize)} ${noun} present when the scan started; the rest ran while it was read.${repeats}`;
  }
  // `scanned` already counts distinct entries; a repeat is not an extra entry.
  if (scanned === totalSize) {
    return `Counted all ${count(scanned)} ${noun}.${repeats}`;
  }
  return `Counted all ${count(scanned)} ${noun}. There were ${count(totalSize)} when the scan started; the count changed while it was read.${repeats}`;
};

// Fits the DataTable's widest datetime ("12/31/2026 12:59:59 PM GMT+10": two-
// digit month, day and hour, and the longest short zone name) plus the cell's
// side padding, so a timestamp is never cut off.
export const DATETIME_COLUMN_WIDTH = 270;
